// Клиент ИИ-ассистента (assistant-api на prod-api, /assistant/ за nginx).
// Чат идёт SSE-стримом через fetch: EventSource не умеет POST с Authorization.
import TokenService from "./token.service";

const ASSISTANT_URL =
    process.env.VUE_APP_ENV_ASSISTANT_URL || "https://api.antilaundry.ru/assistant/api";

export interface IAssistantDraft {
    id: number;
    kind: "action" | "categorize" | "autocat_rule";
    action?: string;
    title: string;
    status: "proposed" | "applied" | "rejected";
    count?: number;
    sum?: number;
    preview?: {
        total: { count: number; sum: number };
        uncategorized: { count: number; sum: number };
    };
}

export type AssistantEvent =
    | { type: "thread"; thread_id: number }
    | { type: "text"; delta: string }
    | { type: "tool"; name: string }
    | { type: "draft"; draft: IAssistantDraft }
    | { type: "done" }
    | { type: "error"; message: string };

export interface IAssistantThread {
    id: number;
    title: string;
    created_at: string;
}

export interface IAssistantHistoryMessage {
    role: "user" | "assistant";
    content: Array<{ type: string; text?: string; name?: string; [k: string]: unknown }>;
    created_at?: string;
}

const authHeaders = (): Record<string, string> => ({
    Authorization: `Bearer ${TokenService.getLocalAccessToken() || ""}`,
});

export async function fetchThreads(): Promise<IAssistantThread[]> {
    const r = await fetch(`${ASSISTANT_URL}/threads`, { headers: authHeaders() });
    if (!r.ok) throw new Error(`threads: HTTP ${r.status}`);
    return (await r.json()).threads;
}

export async function fetchThread(id: number): Promise<{
    thread: { id: number; title: string };
    messages: IAssistantHistoryMessage[];
}> {
    const r = await fetch(`${ASSISTANT_URL}/threads/${id}`, { headers: authHeaders() });
    if (!r.ok) throw new Error(`thread: HTTP ${r.status}`);
    return await r.json();
}

// Непроведённые черновики действий треда (для восстановления карточек)
export async function fetchDrafts(threadId: number): Promise<IAssistantDraft[]> {
    const r = await fetch(`${ASSISTANT_URL}/threads/${threadId}/drafts`, { headers: authHeaders() });
    if (!r.ok) return [];
    return (await r.json()).drafts || [];
}

// Подтвердить действие → сервер зовёт central-эндпоинт кабинета
export async function applyDraft(id: number): Promise<{ applied?: boolean; already?: boolean; command_id?: number | null; error?: string }> {
    const r = await fetch(`${ASSISTANT_URL}/drafts/${id}/apply`, { method: "POST", headers: authHeaders() });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { error: data.error || `HTTP ${r.status}` };
    return data;
}

export async function rejectDraft(id: number): Promise<void> {
    await fetch(`${ASSISTANT_URL}/drafts/${id}/reject`, { method: "POST", headers: authHeaders() });
}

/**
 * Отправка сообщения; onEvent зовётся на каждое SSE-событие.
 * Возвращает функцию отмены (стоп-кнопка).
 */
export function sendMessage(
    threadId: number | null,
    message: string,
    onEvent: (e: AssistantEvent) => void,
): () => void {
    const controller = new AbortController();

    (async () => {
        try {
            const r = await fetch(`${ASSISTANT_URL}/chat`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ thread_id: threadId, message }),
                signal: controller.signal,
            });
            if (!r.ok || !r.body) {
                let msg = `HTTP ${r.status}`;
                try {
                    const j = await r.json();
                    if (j && j.error) msg = j.error;
                } catch { /* тело не JSON — оставляем статус */ }
                onEvent({ type: "error", message: msg });
                return;
            }
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) >= 0) {
                    const chunk = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    for (const line of chunk.split("\n")) {
                        if (!line.startsWith("data: ")) continue; // ': ping' — keepalive
                        try {
                            onEvent(JSON.parse(line.slice(6)) as AssistantEvent);
                        } catch { /* обрывок события — пропускаем */ }
                    }
                }
            }
        } catch (e: unknown) {
            if ((e as Error)?.name !== "AbortError") {
                onEvent({ type: "error", message: "Сеть недоступна или соединение оборвалось" });
            }
        }
    })();

    return () => controller.abort();
}
